import { Button } from './button'
// @ts-strict-ignore
import { Position } from '@blueprintjs/core'
import { Menu, MenuItem } from './menu'
import { Classes, Popover } from './popover'
import { Tooltip } from './tooltip'
import { pascalCase } from 'change-case'
import classNames, { type Value as ClassValue } from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import {
    hideBulkActionModals,
    setModalData,
    setOutOfSyncIds,
    setPayload,
    showBulkActionModal,
} from '../../actions/bulk_actions'
import type { EntityId } from '../../types/constants'
import { setBulkActionProgress as reduxSetBulkActionProgress } from '../../actions/progress'
import { TableName } from '../../config'
import type { ShowBulkActionModal } from '../../constants/reducers/bulk_actions'
import { useInterval } from '../../hooks/useInterval'
import { useUser } from '../../hooks/useUser'
import { BulkActionsIcon } from '../../icons/icons'
import { DefaultSelected } from '../../packages/blueprint-components/src/common'
import type { BulkActionModals } from '../../types/reducers/bulk_actions'
import type { Column } from '../../types/table'
import { axiosGet, axiosPatch } from '../../util/api'
import { OwnedBulkActionStatusDialog } from '../BulkActionsPreviewDialog'
import { Toasts } from '../toaster/Toast'
import {
    fetchBulkActionProgress,
    type FinishedBulkActionRun,
    type State as ReduxState,
} from './../../reducers/index'
import { AddAsinsModal } from './AddAsinsModal'
import { AddSkusModal } from './AddSkusModal'
import type { ProductAdRunCallbacks } from './ProductAdBulkActionModal'
import type { BulkActionStatusCounts } from './BulkActionStatus'
import {
    BooleanModal,
    DateInputModal,
    DeleteModal,
    DropdownInputModal,
    NegativeKeywordModal,
    NumberInputModal,
    PercentageInputModal,
    RadioInputModal,
    StaticSelectModal,
    TargetAcosModal,
    TestBulkRunModal,
} from './BulkActionsModals'
import { OutOfSyncModal } from './OutOfSyncModal'
import { isSynchronousBulkActionEndpoint, resolveBulkActionEndpoint } from './portfolioBulkActions'
import { toastUpgradedToV2Refusal } from '../../features/recipe-upgrades/enable-refusal'
import {
    type BulkActionTerminalOutcome,
    notifyBulkActionRunCompleted,
    extractProgressUuid,
    registerBulkActionRun,
    resolveUnownedBulkActionCompletion,
    terminalResultHasMutation,
} from './bulkActionExecution'

interface State {
    progress: Progress
}

interface Progress {
    bulk_actions: any
    recipes: any
}

interface ExecutionPresentation {
    phase: 'running' | 'succeeded' | 'partially_succeeded' | 'failed'
    title: string
    message?: string
    counts?: BulkActionStatusCounts
    entity?: string
    entityPlural?: string
}

interface OwnedRunContext {
    observer?: ProductAdRunCallbacks
    payload?: any
    dismissed: boolean
    unregister: () => void
}

interface BulkActionEntityWording {
    entity: string
    entityPlural: string
}

const bulkActionEntityWording: Record<string, BulkActionEntityWording> = {
    [TableName.Campaign]: { entity: 'campaign', entityPlural: 'campaigns' },
    [TableName.AdGroup]: { entity: 'ad group', entityPlural: 'ad groups' },
    [TableName.CampaignPlacement]: { entity: 'placement', entityPlural: 'placements' },
    [TableName.Keyword]: { entity: 'keyword', entityPlural: 'keywords' },
    [TableName.Target]: { entity: 'target', entityPlural: 'targets' },
    [TableName.NegativeKeyword]: {
        entity: 'negative keyword',
        entityPlural: 'negative keywords',
    },
    [TableName.CampaignNegativeKeyword]: {
        entity: 'campaign negative keyword',
        entityPlural: 'campaign negative keywords',
    },
    [TableName.NegativeTarget]: {
        entity: 'negative target',
        entityPlural: 'negative targets',
    },
    [TableName.ProductAds]: { entity: 'product ad', entityPlural: 'product ads' },
    [TableName.Portfolio]: { entity: 'portfolio', entityPlural: 'portfolios' },
    [TableName.Recipes]: { entity: 'recipe', entityPlural: 'recipes' },
    [TableName.PublicRecipes]: { entity: 'public recipe', entityPlural: 'public recipes' },
    [TableName.Segments]: { entity: 'segment', entityPlural: 'segments' },
    [TableName.Promotions]: { entity: 'smart campaign', entityPlural: 'smart campaigns' },
}

export const resolveBulkActionEntity = (name: string): BulkActionEntityWording =>
    bulkActionEntityWording[name] ?? { entity: 'item', entityPlural: 'items' }

const selectBulkActionProgress = createSelector(
    (state: State) => state.progress.bulk_actions,
    (bulk_actions) => bulk_actions,
)

type ModelNames =
    | 'state'
    | 'delete'
    | 'dailyBudget'
    | 'defaultBid'
    | 'bid'
    | 'startDate'
    | 'endDate'
    | 'bidStrategy'
    | 'tosPlacement'
    | 'rosPlacement'
    | 'ppPlacement'
    | 'targetAcos'
    | 'portfolio'
    | 'negative_keywords'
    | 'smart_bid'
    | 'enabled'
    | 'recipe-delete'
    | 'recipes-bulk-run'
    | 'promotions:enabled'
    | 'promotions:delete'
    | 'add_asin_to_ad_group'
    | 'add_sku_to_ad_group'
    | 'out_of_sync'

interface BulkActionsProps<T, Extra extends object> {
    className?: ClassValue
    params?: any
    mutableFields?: string[]
    disabled?: boolean
    selectedValues: any
    name: string
    modelName?: ModelNames
    reload: any
    fetchData: any
    setSelectedValues: any
    selectKey: string
    columns?: Column<T, Extra>[]
    id?: number
    bulkActionsInProgressData?: FinishedBulkActionRun[]
    filterOverride?: any
}

export const BulkActions = <T, Extra extends object>({
    params = {},
    mutableFields = [],
    disabled,
    selectedValues,
    name,
    modelName,
    reload,
    fetchData,
    setSelectedValues,
    selectKey,
    columns = [],
    id = null, // used for table widgets only
    bulkActionsInProgressData = null,
    filterOverride = null,
    className,
}: BulkActionsProps<T, Extra>) => {
    const dispatch = useDispatch()
    const bulkActionProgress = useSelector(selectBulkActionProgress)
    const modals = useSelector<ReduxState, BulkActionModals>((store) => store.bulkActions.modals)
    const outOfSyncIds = useSelector<ReduxState, EntityId[]>(
        (store) => store.bulkActions.outOfSyncIds,
    )
    const { hasActiveSubscription, activeProfile } = useUser()
    const modalData = useSelector<ReduxState, any>((store) => store.bulkActions.modalData)
    const payload = useSelector<ReduxState, any>((store) => store.bulkActions.payload)
    const [loading, setLoading] = useState(false)
    const [visibleRunUuid, setVisibleRunUuid] = useState<string>()
    const [executionPresentation, setExecutionPresentation] = useState<ExecutionPresentation>()
    const visibleRunUuidRef = useRef<string>()
    const ownedRuns = useRef(new Map<string, OwnedRunContext>())
    const progressClass =
        name === TableName.Segments
            ? 'Recipes'
            : name === TableName.Promotions
              ? 'Promotions'
              : modelName
                ? pascalCase(modelName)
                : pascalCase(name)
    const selectionScopeProps = useMemo(() => {
        const wording = resolveBulkActionEntity(name)
        return {
            selection: {
                kind: selectedValues?.all ? ('allMatching' as const) : ('selected' as const),
                count: selectedValues?.selectedCount ?? 0,
            },
            ...wording,
        }
    }, [name, selectedValues?.all, selectedValues?.selectedCount])

    useEffect(
        () => () => {
            ownedRuns.current.forEach((context) => context.unregister())
            ownedRuns.current.clear()
        },
        [],
    )

    const handleModelNames = (modelName: ModelNames): string => {
        if (id) {
            return `${modelName}-${id}`
        }

        return modelName
    }

    const _showBulkActionModal = (modelName: ModelNames): ShowBulkActionModal => {
        return showBulkActionModal(handleModelNames(modelName))
    }

    const bulkActions = [
        {
            key: 'state',
            value: (
                <MenuItem
                    key={'state'}
                    text="Set State"
                    onClick={() => dispatch(_showBulkActionModal('state'))}
                />
            ),
        },
        {
            key: 'delete',
            value: (
                <MenuItem
                    key={'delete'}
                    text="Delete"
                    onClick={() => dispatch(_showBulkActionModal('delete'))}
                />
            ),
        },
        {
            key: 'dailyBudget',
            value: (
                <MenuItem
                    key={'dailyBudget'}
                    text="Change Daily Budget"
                    onClick={() => dispatch(_showBulkActionModal('dailyBudget'))}
                />
            ),
        },
        {
            key: 'defaultBid',
            value: (
                <MenuItem
                    key={'defaultBid'}
                    text="Change Default Bid"
                    onClick={() => dispatch(_showBulkActionModal('defaultBid'))}
                />
            ),
        },
        {
            key: 'bid',
            value: (
                <MenuItem
                    key={'bid'}
                    text="Change Bid"
                    onClick={() => dispatch(_showBulkActionModal('bid'))}
                />
            ),
        },
        {
            key: 'startDate',
            value: (
                <MenuItem
                    key={'startDate'}
                    text="Start Date"
                    onClick={() => dispatch(_showBulkActionModal('startDate'))}
                />
            ),
        },
        {
            key: 'targetAcos',
            value: (
                <MenuItem
                    key={'targetAcos'}
                    text="Target Acos"
                    onClick={() => dispatch(_showBulkActionModal('targetAcos'))}
                />
            ),
        },
        {
            key: 'portfolio',
            value: (
                <MenuItem
                    key={'portfolio'}
                    text="Add to Portfolio"
                    onClick={() => dispatch(_showBulkActionModal('portfolio'))}
                />
            ),
        },
        {
            key: 'negative_keywords',
            value: (
                <MenuItem
                    key={'negative_keywords'}
                    text="Add Negative Keywords"
                    onClick={() => {
                        dispatch(_showBulkActionModal('negative_keywords'))
                    }}
                />
            ),
        },
        {
            key: 'smart_bid',
            value: (
                <MenuItem
                    key={'smart_bid'}
                    text="Smart Bid"
                    onClick={() => dispatch(_showBulkActionModal('smart_bid'))}
                />
            ),
        },
        {
            key: 'enabled',
            value: (
                <MenuItem
                    key={'enabled'}
                    text="Enabled"
                    onClick={() => dispatch(_showBulkActionModal('enabled'))}
                />
            ),
        },
        {
            key: 'recipe-delete',
            value: (
                <MenuItem
                    key={'recipe-delete'}
                    text="Delete"
                    onClick={() => dispatch(_showBulkActionModal('recipe-delete'))}
                />
            ),
        },
        {
            key: 'recipes-bulk-run',
            value: (
                <MenuItem
                    key={'recipes-bulk-run'}
                    text="Bulk Run"
                    onClick={() => dispatch(_showBulkActionModal('recipes-bulk-run'))}
                />
            ),
        },
        {
            key: 'promotions:enabled',
            value: (
                <MenuItem
                    key={'promotions:enabled'}
                    text="Enabled"
                    onClick={() => dispatch(_showBulkActionModal('promotions:enabled'))}
                />
            ),
        },
        {
            key: 'promotions:delete',
            value: (
                <MenuItem
                    key={'promotions:delete'}
                    text="Delete"
                    onClick={() => dispatch(_showBulkActionModal('promotions:delete'))}
                />
            ),
        },
        {
            key: 'bidStrategy',
            value: (
                <MenuItem
                    key={'bidStrategy'}
                    text="Change Bidding Strategy"
                    onClick={() => dispatch(_showBulkActionModal('bidStrategy'))}
                />
            ),
        },
        {
            key: 'tosPlacement',
            value: (
                <MenuItem
                    key={'tosPlacement'}
                    text="Change Top of Search %"
                    onClick={() => dispatch(_showBulkActionModal('tosPlacement'))}
                />
            ),
        },
        {
            key: 'rosPlacement',
            value: (
                <MenuItem
                    key={'rosPlacement'}
                    text="Change Rest of Search %"
                    onClick={() => dispatch(_showBulkActionModal('rosPlacement'))}
                />
            ),
        },
        {
            key: 'ppPlacement',
            value: (
                <MenuItem
                    key={'ppPlacement'}
                    text="Change Product Pages %"
                    onClick={() => dispatch(_showBulkActionModal('ppPlacement'))}
                />
            ),
        },
        {
            key: 'endDate',
            value: (
                <MenuItem
                    key={'endDate'}
                    text="End Date"
                    onClick={() => dispatch(_showBulkActionModal('endDate'))}
                />
            ),
        },
    ]

    if (activeProfile?.type == 'vendor') {
        const disabledVerbiage =
            'Selecting "All Rows from All Pages" disables this action. Please choose manual ' +
            'selection or select all items from the current page to proceed with the action.'
        bulkActions.push({
            key: 'ad_group_id',
            value: (
                <Tooltip
                    key="ad_group_id"
                    disabled={!selectedValues?.all}
                    className={Classes.POPOVER2_CONTENT_SIZING}
                    content={<p className="w-80 m-0 p-0">{disabledVerbiage}</p>}
                >
                    <MenuItem
                        disabled={selectedValues?.all}
                        key={'ad_group_id'}
                        text="Add ASINs"
                        onClick={() => dispatch(_showBulkActionModal('add_asin_to_ad_group'))}
                    />
                </Tooltip>
            ),
        })
    } else if (activeProfile?.type == 'seller') {
        const disabledVerbiage =
            'Selecting "All Rows from All Pages" disables this action. Please choose manual ' +
            'selection or select all items from the current page to proceed with the action.'

        bulkActions.push({
            key: 'ad_group_id',
            value: (
                <Tooltip
                    key="ad_group_id"
                    disabled={!selectedValues?.all}
                    className={Classes.POPOVER2_CONTENT_SIZING}
                    content={<p className="w-80 m-0 p-0"> {disabledVerbiage}</p>}
                >
                    <MenuItem
                        disabled={selectedValues?.all}
                        key={'ad_group_id'}
                        text="Add SKUs"
                        onClick={() => dispatch(_showBulkActionModal('add_sku_to_ad_group'))}
                    />
                </Tooltip>
            ),
        })
    }

    // Table widgets namespace their modal keys by widget id, so reading the bare
    // `portfolio` flag would route a widget submission to the async endpoint.
    const portfolioModalKey = handleModelNames('portfolio')

    const endpoint = useMemo(
        () => resolveBulkActionEndpoint(name, Boolean(modals[portfolioModalKey])),
        [name, modals, portfolioModalKey],
    )

    const handleTerminalRun = (uuid: string, outcome?: BulkActionTerminalOutcome) => {
        const context = ownedRuns.current.get(uuid)
        if (!context) {
            return
        }

        const terminalRequest = outcome?.response
            ? Promise.resolve(outcome.response)
            : outcome?.error
              ? Promise.reject(outcome.error)
              : axiosGet('bulk-actions/run-info', { uuid })
        terminalRequest
            .then((res) => {
                const presentation = terminalPresentation(name, res.data)
                if (terminalResultHasMutation(res.data)) {
                    if (!outcome?.refreshed) {
                        reload(true)
                    }
                    setSelectedValues(DefaultSelected)
                }
                if (context.observer) {
                    context.observer.onSuccess(res.data)
                } else if (outcome?.presented) {
                    return
                } else if (!context.dismissed && visibleRunUuidRef.current === uuid) {
                    setExecutionPresentation(presentation)
                } else {
                    toastTerminalPresentation(presentation)
                }
            })
            .catch((err) => {
                if (err?.response?.status === 409) {
                    // Product-ad runs do not carry the standard update payload and own
                    // their failure UI through their observer.
                    if (!context.payload?.update) {
                        if (context.observer) {
                            context.observer.onFailure(err)
                        } else {
                            Toasts.warning(
                                err?.response?.data?.message ??
                                    'The product ads could not be created because the selected items were out of sync.',
                            )
                        }
                        return
                    }

                    dismissVisibleExecution()
                    console.log(
                        'bulk action run error',
                        err.response.data,
                        'with payload',
                        context.payload,
                    )
                    dispatch(setPayload(context.payload))
                    dispatch(setModalData(context.payload.update))
                    // MER-2802: conflict response keys are bigint-safe strings.
                    const ids: EntityId[] = Object.keys(err.response.data ?? {})
                    dispatch(setOutOfSyncIds(ids))
                    dispatch(hideBulkActionModals())
                    dispatch(_showBulkActionModal('out_of_sync'))
                    Toasts.warning(
                        ids.length +
                            ' items were out of sync. Please review the changes and retry.',
                    )
                    return
                }

                const message =
                    err?.response?.data?.message ??
                    err?.message ??
                    'The bulk action did not complete.'
                if (context.observer) {
                    context.observer.onFailure(err)
                } else if (!context.dismissed && visibleRunUuidRef.current === uuid) {
                    setExecutionPresentation({
                        phase: 'failed',
                        title: `Could not update ${name.toLowerCase()}`,
                        message,
                    })
                } else {
                    Toasts.error(message)
                }
            })
            .finally(() => {
                context.unregister()
                ownedRuns.current.delete(uuid)
            })
    }

    const showExecution = (uuid: string | undefined, presentation: ExecutionPresentation) => {
        visibleRunUuidRef.current = uuid
        setVisibleRunUuid(uuid)
        setExecutionPresentation(presentation)
    }

    const dismissVisibleExecution = () => {
        const uuid = visibleRunUuidRef.current
        if (uuid) {
            const context = ownedRuns.current.get(uuid)
            if (context) {
                context.dismissed = true
            }
        }
        visibleRunUuidRef.current = undefined
        setVisibleRunUuid(undefined)
        setExecutionPresentation(undefined)
    }

    const ownAsyncRun = (
        uuid?: string,
        observer?: ProductAdRunCallbacks,
        runPayload?: any,
        awaitingAcceptance = false,
    ) => {
        if (uuid) {
            const unregister = registerBulkActionRun(uuid, handleTerminalRun, awaitingAcceptance)
            const context: OwnedRunContext = {
                observer,
                payload: runPayload,
                dismissed: false,
                unregister,
            }
            ownedRuns.current.set(uuid, context)
            if (!observer) {
                showExecution(uuid, {
                    phase: 'running',
                    title: awaitingAcceptance
                        ? `Starting ${name.toLowerCase()} update`
                        : `Updating ${name.toLowerCase()}`,
                    message: awaitingAcceptance
                        ? 'Waiting for the bulk-action service to accept this action.'
                        : 'The source table will refresh when this action finishes.',
                })
            }

            return () => {
                const ownedContext = ownedRuns.current.get(uuid)
                if (ownedContext === context) {
                    ownedContext.dismissed = true
                    ownedContext.observer = undefined
                }
            }
        } else if (!observer) {
            showExecution(undefined, {
                phase: 'failed',
                title: 'Bulk action progress unavailable',
                message: 'The action started without a progress ID. Refresh the page to check it.',
            })
        }
    }

    const onSubmit = (update) => {
        dismissVisibleExecution()
        const parsedParams = handleParams({ ...params })
        // Segments are actually recipes (version 2), so use 'Recipes' as the class name
        let output = {
            ...parsedParams,
            selection: selectedValues,
            update,
            class: progressClass,
        }

        if (filterOverride) {
            output = {
                ...output,
                filter: {
                    ...output.filter,
                    ...filterOverride,
                },
            }
        }

        dispatch(setPayload(output))
        setLoading(true)

        axiosPatch(endpoint, output)
            .then((res) => {
                // If we have a synchronous bulk action...
                if (isSynchronousBulkActionEndpoint(endpoint)) {
                    dispatch(hideBulkActionModals())
                    dispatch(setModalData({}))
                    reload(true)
                    setSelectedValues(DefaultSelected)
                    setLoading(false)
                    setExecutionPresentation(terminalPresentation(name, res.data))
                    return
                }

                const progress = res.data?.progress
                const awaitingAcceptance = res.data?.progress_pending === true
                const uuid = awaitingAcceptance
                    ? res.data?.request_uuid
                    : extractProgressUuid(progress, name)
                ownAsyncRun(uuid, undefined, output, awaitingAcceptance)
                setLoading(false)
                if (!uuid) {
                    Toasts.error('The bulk action started without a progress ID. Please refresh.')
                }
                // reload(true)
                dispatch(hideBulkActionModals())
                dispatch(setModalData({}))

                if (endpoint === 'bulk-actions/update') {
                    dispatch(reduxSetBulkActionProgress(progress ?? {}))
                }
            })
            .catch((err) => {
                // These endpoints answer a failure with a plain message body, not the
                // id-keyed out-of-sync map the async path returns. axiosGet/axiosPatch's
                // shared handler has already toasted it, except a 409.
                if (isSynchronousBulkActionEndpoint(endpoint)) {
                    toastUpgradedToV2Refusal(err)
                    setLoading(false)
                    return
                }

                if (err?.response?.status === 409) {
                    dispatch(setModalData(output.update))
                    const ids: EntityId[] = Object.keys(err.response.data ?? {})
                    dispatch(setOutOfSyncIds(ids))
                    dispatch(hideBulkActionModals())
                    dispatch(_showBulkActionModal('out_of_sync'))
                    Toasts.warning(
                        ids.length +
                            ' items were out of sync. Please review the changes and retry.',
                    )
                    // reload()
                }
                setLoading(false)
            })
    }

    const menu = (
        <Menu>
            {bulkActions.map((item) => {
                if (mutableFields.includes(item.key)) {
                    return item.value
                }
            })}
        </Menu>
    )

    const modalProps = (key) => {
        return {
            loading,
            onSubmit,
            isOpen: modals[key],
            setIsOpen: () => dispatch(hideBulkActionModals()),
        }
    }

    useInterval(() => {
        if (name.toLowerCase() == 'recipes' || bulkActionsInProgressData !== null) {
            return
        }
        // @ts-expect-error
        dispatch(fetchBulkActionProgress()).then((finishedUuids) => {
            finishedUuids.forEach(({ uuid, progressClass: completedProgressClass }) => {
                if (completedProgressClass !== progressClass) {
                    return
                }
                if (notifyBulkActionRunCompleted(uuid) === 'unowned') {
                    resolveUnownedBulkActionCompletion(uuid, (outcome) => {
                        if (outcome.response) {
                            let refreshed = false
                            if (terminalResultHasMutation(outcome.response.data)) {
                                reload(true)
                                refreshed = true
                            }
                            toastTerminalPresentation(
                                terminalPresentation(name, outcome.response.data),
                            )
                            return { presented: true, refreshed }
                        }
                        const message =
                            outcome.error?.response?.data?.message ??
                            outcome.error?.message ??
                            'The bulk action did not complete.'
                        Toasts.error(message)
                        return { presented: true, refreshed: false }
                    })
                }
            })
        })
    }, 1000)

    useEffect(() => {
        if (name.toLowerCase() == 'recipes' || bulkActionsInProgressData === null) {
            return
        }

        bulkActionsInProgressData.forEach(({ uuid }) => notifyBulkActionRunCompleted(uuid))
    }, [bulkActionsInProgressData])

    const ownedProgress = useMemo(() => {
        if (!visibleRunUuid) {
            return undefined
        }

        return Object.values(bulkActionProgress ?? {}).find(
            (progress: any) =>
                progress?.uuid === visibleRunUuid || progress?.extra?.uuid === visibleRunUuid,
        )
    }, [visibleRunUuid, bulkActionProgress]) as any

    useEffect(() => {
        if (!ownedProgress) {
            return
        }
        setExecutionPresentation((current) =>
            current?.phase === 'running' && current.title.startsWith('Starting ')
                ? {
                      ...current,
                      title: `Updating ${name.toLowerCase()}`,
                      message: 'The source table will refresh when this action finishes.',
                  }
                : current,
        )
    }, [name, ownedProgress])

    const _modalProps = (modalName: string) => {
        if (id) {
            return { ...modalProps(`${modalName}-${id}`) }
        }

        return { ...modalProps(modalName) }
    }

    return (
        <>
            {modals.add_asin_to_ad_group && (
                <AddAsinsModal
                    selectedValues={selectedValues}
                    onAsyncAcknowledged={(uuid, progress, callbacks) => {
                        const dismissRun = ownAsyncRun(uuid, callbacks)
                        dispatch(reduxSetBulkActionProgress(progress))
                        return dismissRun
                    }}
                    {..._modalProps('add_asin_to_ad_group')}
                />
            )}
            {modals.add_sku_to_ad_group && (
                <AddSkusModal
                    selectedValues={selectedValues}
                    onAsyncAcknowledged={(uuid, progress, callbacks) => {
                        const dismissRun = ownAsyncRun(uuid, callbacks)
                        dispatch(reduxSetBulkActionProgress(progress))
                        return dismissRun
                    }}
                    {..._modalProps('add_sku_to_ad_group')}
                />
            )}
            <OwnedBulkActionStatusDialog
                isOpen={Boolean(executionPresentation)}
                phase={executionPresentation?.phase ?? 'running'}
                title={executionPresentation?.title ?? 'Bulk action in progress'}
                message={executionPresentation?.message}
                counts={executionPresentation?.counts}
                entity={executionPresentation?.entity}
                entityPlural={executionPresentation?.entityPlural}
                progressPercent={
                    executionPresentation?.phase === 'running' ? ownedProgress?.progress : undefined
                }
                onClose={dismissVisibleExecution}
            />
            <OutOfSyncModal
                payload={payload}
                onClose={() => {
                    dispatch(hideBulkActionModals())
                }}
                isOpen={_modalProps('out_of_sync').isOpen}
                columns={columns}
                setModalData={(v) => dispatch(setModalData(v))}
                modalData={modalData}
                selectKey={selectKey}
                name={name}
                outOfSyncIds={outOfSyncIds}
                // previouslySelectedValues={selectedValues}
                endpoint={endpoint}
                onRetryAcknowledged={(acknowledgement, retryPayload) => {
                    dispatch(setPayload(retryPayload))
                    const progress = acknowledgement.data?.progress
                    const awaitingAcceptance = acknowledgement.data?.progress_pending === true
                    const uuid = awaitingAcceptance
                        ? acknowledgement.data?.request_uuid
                        : extractProgressUuid(progress, name)
                    ownAsyncRun(uuid, undefined, retryPayload, awaitingAcceptance)
                    if (progress) {
                        dispatch(reduxSetBulkActionProgress(progress))
                    }
                    if (!uuid) {
                        Toasts.error(
                            'The bulk action started without a progress ID. Please refresh.',
                        )
                    }
                }}
            />
            <NumberInputModal
                name={'Daily Budget'}
                rawName={'daily_budget'}
                {...selectionScopeProps}
                {..._modalProps('dailyBudget')}
            />
            <NumberInputModal
                name={'Default Bid'}
                rawName={'default_bid'}
                {...selectionScopeProps}
                {..._modalProps('defaultBid')}
            />
            <NumberInputModal name={'Bid'} {...selectionScopeProps} {..._modalProps('bid')}>
                <p className="m-0 mb-[18px] text-[13px] leading-[18px] text-[#5f6b7c]">
                    Your minimum and maximum bid settings still apply.
                </p>
            </NumberInputModal>
            <DateInputModal
                name={'Date Start'}
                rawName={'start_date'}
                {...selectionScopeProps}
                {..._modalProps('startDate')}
            />
            <DateInputModal
                name={'End Date'}
                rawName={'end_date'}
                clearable
                setLabel="Set end date"
                clearLabel="No end date"
                {...selectionScopeProps}
                {..._modalProps('endDate')}
            />
            <RadioInputModal name={'State'} {...selectionScopeProps} {..._modalProps('state')} />
            <TargetAcosModal
                name={'Target ACOS'}
                rawName={'target_acos'}
                {...selectionScopeProps}
                {..._modalProps('targetAcos')}
            />
            {modals['negative_keywords'] && (
                <NegativeKeywordModal {..._modalProps('negative_keywords')} />
            )}
            <DropdownInputModal
                fetchData={fetchData['portfolio']}
                name={'Add to Portfolio'}
                rawName={'portfolio'}
                allowUnassigned
                {...selectionScopeProps}
                {..._modalProps('portfolio')}
            />
            <DeleteModal
                name={'Delete Portfolios'}
                rawName={'portfolios'}
                {...selectionScopeProps}
                {..._modalProps('delete')}
            />
            <StaticSelectModal
                name={'Bidding Strategy'}
                rawName={'bid_strategy'}
                {...selectionScopeProps}
                options={[
                    // Labels are Amazon's user-facing wording; the values are the
                    // product vocabulary the engine expects, NOT Amazon's transport
                    // enum. The engine maps SALES -> SALES_UP_AND_DOWN and
                    // NONE -> MANUAL.
                    { label: 'Dynamic bids \u2013 down only', value: 'SALES_DOWN_ONLY' },
                    { label: 'Dynamic bids \u2013 up and down', value: 'SALES' },
                    { label: 'Fixed bids', value: 'NONE' },
                ]}
                {..._modalProps('bidStrategy')}
            />
            <PercentageInputModal
                name={'Top of Search %'}
                rawName={'tos_placement'}
                {...selectionScopeProps}
                {..._modalProps('tosPlacement')}
            />
            <PercentageInputModal
                name={'Rest of Search %'}
                rawName={'ros_placement'}
                {...selectionScopeProps}
                {..._modalProps('rosPlacement')}
            />
            <PercentageInputModal
                name={'Product Pages %'}
                rawName={'pp_placement'}
                {...selectionScopeProps}
                {..._modalProps('ppPlacement')}
            />
            <BooleanModal
                name={'Toggle Smart Bids'}
                label="Smart Bid"
                rawName={'smart_bid'}
                {...selectionScopeProps}
                {..._modalProps('smart_bid')}
            />
            <BooleanModal
                name={name === TableName.Segments ? 'Toggle Segments' : 'Toggle Recipes'}
                label="Enabled"
                rawName={'enabled'}
                {...selectionScopeProps}
                {..._modalProps('enabled')}
            />

            <DeleteModal
                name={name === TableName.Segments ? 'Delete Segments' : 'Delete Recipes'}
                rawName="recipes"
                {...selectionScopeProps}
                {..._modalProps('recipe-delete')}
            />
            <TestBulkRunModal
                name={name === TableName.Segments ? 'Bulk Run Segments' : 'Bulk Run Recipes'}
                rawName="recipes"
                {...selectionScopeProps}
                {..._modalProps('recipes-bulk-run')}
            />

            <BooleanModal
                name={'Toggle Smart Campaigns'}
                label="Enabled"
                rawName={'promotions:enabled'}
                {...selectionScopeProps}
                {..._modalProps('promotions:enabled')}
            />
            <DeleteModal
                name={'Delete Smart Campaigns'}
                rawKey={'promotions:delete'}
                rawName={'promotions'}
                {...selectionScopeProps}
                {..._modalProps('promotions:delete')}
            />

            <Popover
                content={menu}
                position={Position.BOTTOM_LEFT}
                disabled={disabled || !hasActiveSubscription}
            >
                <Tooltip
                    disabled={hasActiveSubscription}
                    content={
                        <div>Bulk actions are disabled until you have an active subscription</div>
                    }
                >
                    <Button
                        icon={<BulkActionsIcon />}
                        className={classNames('rounded-lg', className)}
                        minimal
                        outlined
                        disabled={disabled || !hasActiveSubscription}
                    >
                        <span className="whitespace-nowrap">Bulk Actions</span>
                    </Button>
                </Tooltip>
            </Popover>
        </>
    )
}

const handleParams = (params) => {
    if (
        params?.filter &&
        params?.filter?.campaign_id &&
        typeof params.filter?.campaign_id == 'object'
    ) {
        params.filter.campaign_id = params.filter.campaign_id.campaign_id
    }

    if (
        params?.filter &&
        params?.filter?.ad_group_id &&
        typeof params.filter?.ad_group_id == 'object'
    ) {
        params.filter.ad_group_id = params.filter.ad_group_id.ad_group_id
    }

    return params
}

/**
 * The result view is the only place a user learns whether the table they were
 * looking at now reflects the change, so the refresh outcome is stated
 * explicitly rather than left to an optional server message.
 *
 * `succeeded` absent is distinct from `succeeded === 0`: the service reported
 * nothing, which is not the same as reporting that nothing changed.
 */
export const terminalPresentation = (name: string, result: any): ExecutionPresentation => {
    const succeeded = result?.counts?.success
    const failed = result?.counts?.failed
    const hasCounts = typeof succeeded === 'number' || typeof failed === 'number'
    // The wording map gives "ad group"/"ad groups", not a lower-cased TableName.
    const wording = resolveBulkActionEntity(name)
    const entity = wording.entityPlural

    if (!hasCounts) {
        return {
            phase: 'succeeded',
            title: `${name} updated`,
            message:
                result?.message ??
                `Amazon did not report per-item counts. The ${entity} table has been refreshed.`,
            entity: wording.entity,
            entityPlural: entity,
        }
    }

    const changed = succeeded ?? 0
    const rejected = failed ?? 0

    if (changed > 0) {
        return {
            phase: rejected > 0 ? 'partially_succeeded' : 'succeeded',
            title: rejected > 0 ? `${name} partially updated` : `${name} updated`,
            // The outcome line carries the counts. A refresh after a successful
            // run is self-evident, so only a server message earns a second line.
            message: result?.message,
            counts: { succeeded, failed },
            entity: wording.entity,
            entityPlural: entity,
        }
    }

    // Nothing changed. Amazon rejecting every item and Amazon skipping every
    // item are different outcomes and must not share the same headline.
    return {
        phase: 'failed',
        title: rejected > 0 ? `${name} could not be updated` : `No ${entity} were changed`,
        // Nothing succeeded, so "not refreshed" is implied. Only the retry
        // affordance is worth a second line.
        message: result?.message ?? 'Your selection has been kept.',
        counts: { succeeded, failed },
        entity: wording.entity,
        entityPlural: entity,
    }
}

export const toastTerminalPresentation = (presentation: ExecutionPresentation) => {
    if (presentation.phase === 'failed') {
        Toasts.error(presentation.message ?? presentation.title)
        return
    }

    Toasts.success(presentation.message ?? presentation.title)
}
